import React, { forwardRef, RefObject, useContext } from 'react';
import {
  UsfCard,
  CardTitle,
  SeatCountSmallIconWrapper,
  BoldNumber,
  SeatCountUsfBox,
} from 'spw-programming-projects/program-summary/single-page-summary/styles';
import { Box, Grid } from '@mui/material';
import { valueOrZero, numberWithCommasOrDash } from 'spw-shared/utils';
import { CardValueTitle } from 'spw-programming-projects/program-summary/components/styles';
import { useDialogStatus } from 'spw-components/dialogs/total-program/use-dialog-status';
import SeatCountUsfDialog from 'spw-components/dialogs/total-program/seat-count-usf-dialog';
import { SEAT_COUNT_USF_VALUES } from 'spw-components/dialogs/total-program/utils';
import useUnitConversion from 'spw-components/unit-conversion/hooks';
import { useCardIcon } from 'spw-esa-projects/program-summary/cards/hooks';
import { ProgramSummaryCardType } from 'spw-esa-projects/program-summary/icon-selector-popover';
import CardIcon from 'spw-esa-projects/program-summary/card-icon';
import { ProgrammingSummaryCardsContext } from '../program-summary-tabs/summary-card-context';
import { PlainWorksheet } from '../../../../models/Worksheet';

interface ISeatCountUsfProps {
  showRsf: boolean;
  totalAvailableUsf: number;
  totalProgramRsf: number;
  totalProgramUsf: number;
  futureWorkSeats: number;
  isTotalProgramScenario: boolean;
  totalProgramWorksheets?: PlainWorksheet[];
}

const SeatCountUsf = forwardRef<HTMLElement, ISeatCountUsfProps>(
  (
    {
      showRsf,
      totalAvailableUsf,
      totalProgramRsf,
      totalProgramUsf,
      futureWorkSeats,
      isTotalProgramScenario,
      totalProgramWorksheets,
    },
    ref
  ) => {
    const { getUnit, convertArea } = useUnitConversion();

    const cardIcon = useCardIcon(ProgramSummaryCardType.seatCountUsf);
    const { selectedCardIcon, onClickCardIcon } = useContext(
      ProgrammingSummaryCardsContext
    );

    const handleIconClick = (event: React.MouseEvent<HTMLDivElement>) => {
      onClickCardIcon(event, cardIcon);
    };

    const isIconCardSelected =
      selectedCardIcon?.cardType === ProgramSummaryCardType.seatCountUsf;

    const programRsfWorkSeatDensity = Number(
      valueOrZero(convertArea(totalProgramRsf) / futureWorkSeats)
    );
    const totalUsfWorkSeatDensity = Number(
      valueOrZero(convertArea(totalAvailableUsf) / futureWorkSeats)
    );

    const usfRelation = Number(
      valueOrZero(convertArea(totalProgramUsf) / futureWorkSeats)
    );

    const {
      showDialog,
      openDialog,
      valueType,
      dialogValue,
      setShowDialog,
    } = useDialogStatus(isTotalProgramScenario);

    const showTotalUsfFutureWorkSeats = () => {
      openDialog(
        SEAT_COUNT_USF_VALUES.TOTAL_USF_FUTURE_WORK_SEATS,
        totalUsfWorkSeatDensity
      );
    };

    const showProgramUsfFutureWorkSeats = () => {
      openDialog(
        SEAT_COUNT_USF_VALUES.PROGRAM_USF_FUTURE_WORK_SEATS,
        usfRelation
      );
    };

    return (
      <>
        {showDialog && isTotalProgramScenario && !window.SPWExport && (
          <SeatCountUsfDialog
            totalProgramWorksheets={totalProgramWorksheets}
            showDialog={showDialog}
            setShowDialog={setShowDialog}
            value={dialogValue}
            valueType={valueType}
            showTotalUsfFutureWorkSeats={showTotalUsfFutureWorkSeats}
            showProgramUsfFutureWorkSeats={showProgramUsfFutureWorkSeats}
            anchorRef={ref}
          />
        )}
        <div ref={ref as RefObject<HTMLDivElement>}>
          <UsfCard>
            <CardTitle
              className="mb-8"
              data-testid="programming_ps_seatcount_usf_title"
            >
              SEAT COUNT{' '}
              {showRsf
                ? `${getUnit('area')} + ${getUnit('rentable-area')}`
                : getUnit('area')}
            </CardTitle>
            <Grid container gap={0}>
              <Grid item xs={6}>
                <SeatCountUsfBox className="left">
                  <Box display="flex" alignItems="center">
                    <SeatCountSmallIconWrapper>
                      <CardIcon
                        iconPath={`/images/space-icons/${cardIcon.iconPath}`}
                        onClick={handleIconClick}
                        height={32}
                        width={32}
                        containerHeight={32}
                        isSelected={isIconCardSelected}
                      />
                    </SeatCountSmallIconWrapper>
                    <Box
                      className={`${isTotalProgramScenario ? 'highlight' : ''}`}
                      onClick={showProgramUsfFutureWorkSeats}
                    >
                      <BoldNumber data-testid="programming_ps_seatcount_usf_programUSFOverWorkseats">
                        {numberWithCommasOrDash(usfRelation)}
                      </BoldNumber>
                      <CardValueTitle>
                        Program {getUnit('area')} /
                      </CardValueTitle>
                      <CardValueTitle>Work Seats</CardValueTitle>
                    </Box>
                  </Box>
                </SeatCountUsfBox>
              </Grid>
              {showRsf && (
                <Grid item xs={6}>
                  <SeatCountUsfBox className="right">
                    <Box display="flex" alignItems="center">
                      <SeatCountSmallIconWrapper>
                        <CardIcon
                          iconPath={`/images/space-icons/${cardIcon.iconPath}`}
                          onClick={handleIconClick}
                          height={32}
                          width={32}
                          containerHeight={32}
                          isSelected={isIconCardSelected}
                        />
                      </SeatCountSmallIconWrapper>
                      <Box>
                        <BoldNumber data-testid="programming_ps_seatcount_usf_totalUSFOverWorkseats">
                          {numberWithCommasOrDash(programRsfWorkSeatDensity)}
                        </BoldNumber>

                        <CardValueTitle>
                          Program {getUnit('rentable-area')} /
                        </CardValueTitle>
                        <CardValueTitle>Work Seats</CardValueTitle>
                      </Box>
                    </Box>
                  </SeatCountUsfBox>
                </Grid>
              )}
            </Grid>
          </UsfCard>
        </div>
      </>
    );
  }
);

const MemoizedSeatCountUsf = React.memo(SeatCountUsf);

MemoizedSeatCountUsf.whyDidYouRender = true;

export { MemoizedSeatCountUsf as SeatCountUsf };
